<template>
  <LandingSection
    id="advantages"
    :title="t('landingB.advantagesB.title')"
    section-class="landing-section relative overflow-hidden py-24 lg:py-28"
    title-class="landing-heading landing-advantages-heading text-4xl"
    container-class="landing-advantages-shell container mx-auto px-4 sm:px-6"
  >
    <div class="landing-advantages-rows">
      <article
        v-for="(advantage, index) in advantages"
        :key="advantage.titleKey"
        class="landing-advantage-row"
        :class="{ 'is-reversed': index % 2 === 1 }"
      >
        <!-- 主视觉：hero 轮播图，按原始 2:3 比例完整渲染，不裁切，便于阅读图中内容 -->
        <figure class="landing-advantage-media">
          <img
            :src="advantage.image"
            :alt="advantage.title"
            class="landing-advantage-image"
            loading="lazy"
            decoding="async"
          />
        </figure>

        <div class="landing-advantage-copy">
          <span class="landing-advantage-serial-ghost" aria-hidden="true">{{ advantage.serial }}</span>

          <span class="landing-advantage-index">
            <span class="landing-advantage-index-num">{{ advantage.serial }}</span>
            <span class="landing-advantage-index-rule" aria-hidden="true"></span>
          </span>

          <h3 class="landing-advantage-title landing-card-title">{{ advantage.title }}</h3>
          <p class="landing-advantage-description">{{ advantage.description }}</p>

          <div class="landing-advantage-meter" aria-hidden="true">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </article>
    </div>
  </LandingSection>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import LandingSection from '@/components/landing/LandingSection.vue'
import {
  landingAdvantagesB as advantagesSource,
  landingFeatures as featureImages,
} from '@/core/data/landingContent'

const { t } = useI18n()

// B 版专属文案（方案二·品类区隔型），按索引挂载 hero 轮播四图，图文顺序与配图一一对应。
const advantages = computed(() =>
  advantagesSource.map((advantage, index) => ({
    ...advantage,
    serial: String(index + 1).padStart(2, '0'),
    title: t(advantage.titleKey),
    description: t(advantage.descriptionKey),
    image: featureImages[index]?.backgroundImage || '',
  }))
)
</script>

<style scoped>
:deep(.landing-advantages-shell) {
  position: relative;
  z-index: 1;
}

:deep(.landing-advantages-heading) {
  margin-bottom: clamp(2.25rem, 5vw, 3.75rem);
  text-align: center;
}

/* 整行展示：图文交替排布，图片保持原始比例、足够大以便读图 */
.landing-advantages-rows {
  display: grid;
  gap: clamp(2.25rem, 4.5vw, 3.75rem);
}

.landing-advantage-row {
  display: grid;
  grid-template-columns: minmax(0, 0.42fr) minmax(0, 0.58fr);
  align-items: center;
  gap: clamp(1.75rem, 4vw, 4rem);
}

/* 偶数行图文左右互换，制造编辑式节奏 */
.landing-advantage-row.is-reversed {
  grid-template-columns: minmax(0, 0.58fr) minmax(0, 0.42fr);
}

.landing-advantage-row.is-reversed .landing-advantage-media {
  order: 2;
}

.landing-advantage-row.is-reversed .landing-advantage-copy {
  order: 1;
}

/* 主视觉：原始 2:3 竖图，完整渲染，不裁切 */
.landing-advantage-media {
  position: relative;
  z-index: 1;
  margin: 0;
  width: 100%;
  max-width: 22rem;
  margin-inline: auto;
  border-radius: 1.4rem;
  overflow: hidden;
  isolation: isolate;
  border: 1px solid color-mix(in srgb, var(--zinc-50) 14%, transparent);
  background: color-mix(in srgb, var(--surface-card-hover) 80%, transparent);
  box-shadow: 0 26px 54px color-mix(in srgb, var(--shadow-color) 26%, transparent);
  transition: border-color 0.45s ease, box-shadow 0.45s ease,
    transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  /* hover 放大时以偏下位置为原点，减少向上溢出覆盖 title */
  transform-origin: center 60%;
}

.landing-advantage-image {
  width: 100%;
  height: auto; /* 保持原始 2:3 比例，不裁切 */
  display: block;
  transform: scale(1.002);
  transition: transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
}

.landing-advantage-row:hover .landing-advantage-media {
  border-color: color-mix(in srgb, var(--theme-primary) 42%, transparent);
  box-shadow: 0 34px 68px color-mix(in srgb, var(--shadow-color) 34%, transparent),
    0 0 0 1px color-mix(in srgb, var(--theme-primary) 18%, transparent);
  /* 放大 ~18%：足够阅读图中内容，同时减少向上溢出覆盖 title；transform 不触发重排，组件整体高度不变 */
  transform: scale(1.18);
  z-index: 5;
}

/* hover 时不再额外缩放内层图片，避免与整框放大叠加导致裁切 */
.landing-advantage-row:hover .landing-advantage-image {
  transform: scale(1);
}

/* 文案列：与图片形成清晰的左右排版关系 */
.landing-advantage-copy {
  position: relative;
  z-index: 1;
  display: grid;
  gap: 1.15rem;
  max-width: 34rem;
}

/* 大号幽灵序号，置于文案列右上角后方，作为签名式装饰（继承原组件 DNA） */
.landing-advantage-serial-ghost {
  position: absolute;
  top: -2.25rem;
  right: 0;
  z-index: 0;
  font-family: 'Montserrat', sans-serif;
  font-size: clamp(5rem, 12vw, 9rem);
  font-weight: 700;
  line-height: 0.8;
  letter-spacing: -0.04em;
  color: color-mix(in srgb, var(--theme-primary) 8%, transparent);
  pointer-events: none;
}

/* 序号标记：小号数字 + 短线，作为标题前的编辑式引导 */
.landing-advantage-index {
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
}

.landing-advantage-index-num {
  font-family: 'Montserrat', sans-serif;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--theme-primary);
}

.landing-advantage-index-rule {
  width: 2.4rem;
  height: 1px;
  background: color-mix(in srgb, var(--theme-primary) 50%, var(--border-default));
}

.landing-advantage-title {
  margin: 0;
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  font-size: clamp(1.5rem, 2.4vw, 2rem);
  line-height: 1.14;
  letter-spacing: -0.01em;
  color: var(--text-primary);
}

.landing-advantage-description {
  margin: 0;
  max-width: 46ch;
  color: var(--text-secondary);
  font-size: 1.02rem;
  line-height: 1.74;
}

/* 装饰 meter 条，继承原组件底部细节 */
.landing-advantage-meter {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-top: 0.4rem;
}

.landing-advantage-meter span {
  display: block;
  height: 0.38rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--theme-primary) 20%, var(--surface-card-hover));
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), background 0.35s ease;
  transform-origin: left center;
}

.landing-advantage-meter span:nth-child(1) {
  width: 3.2rem;
}

.landing-advantage-meter span:nth-child(2) {
  width: 1.8rem;
  background: color-mix(in srgb, var(--theme-accent) 24%, var(--surface-card-hover));
}

.landing-advantage-meter span:nth-child(3) {
  width: 4.6rem;
}

.landing-advantage-row:hover .landing-advantage-meter span {
  transform: scaleX(1.08);
}

/* 中等屏幕：堆叠为单列，图片在上、文字在下 */
@media (max-width: 920px) {
  .landing-advantage-row,
  .landing-advantage-row.is-reversed {
    grid-template-columns: minmax(0, 1fr);
    gap: clamp(1.75rem, 5vw, 2.5rem);
  }

  .landing-advantage-row.is-reversed .landing-advantage-media,
  .landing-advantage-row.is-reversed .landing-advantage-copy {
    order: 0;
  }

  .landing-advantage-media {
    /* 移动端撑满行宽：父容器已有 px-4/px-6 左右 padding 作为自然留白 */
    max-width: 100%;
    /* 更宽的图片对应更小的圆角比例，保持视觉协调 */
    border-radius: 0.9rem;
  }

  .landing-advantage-copy {
    max-width: 42rem;
  }

  .landing-advantage-serial-ghost {
    top: -1.5rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .landing-advantage-media,
  .landing-advantage-image,
  .landing-advantage-meter span {
    transition: none;
  }

  .landing-advantage-row:hover .landing-advantage-image {
    transform: none;
  }
}
</style>
